import api from './client'

/**
 * Client for the one-shot deterministic Unit Test (Live Preview).
 *
 * The agentic test→fix loop now runs through the Ask-AI agent (the Unit Test
 * button dispatches `twasta:agent-run-unit-test`, handled in AskAIDock, which
 * delegates to the unit-test-runner sub-agent). This module only keeps the
 * one-shot test used as the LLM-mode fallback (no CLI / sub-agents there).
 */

/** One-shot deterministic test (no fixing) — the LLM-mode fallback. */
export async function runUnitTestOnce(
  project: string,
  objName: string,
  objType = 'T',
  scope: 'full' | 'add_only' = 'full',
) {
  const res = await api.post('/claudeagent/unit-test/run', {
    project,
    obj_name: objName,
    obj_type: objType,
    scope,
  })
  return res.data as UnitTestReport
}

// ── shared shapes (mirror the backend report envelope) ──────────────────────

export interface UnitTestFieldError {
  field?: string
  rule?: string
  message?: string
}

export interface UnitTestFailure {
  step?: string
  code?: string
  message?: string
  field_errors?: UnitTestFieldError[]
  traceback?: string
}

export interface UnitTestStep {
  name: string
  status: 'PASS' | 'FAIL' | 'SKIP'
  detail?: string | null
  issue?: UnitTestFailure | null
  /** The check it belongs to (absent = "record"). */
  check?: string
}

/** One check of a unit test: the record lifecycle, or one action button. */
export interface UnitTestCheck {
  /** "record" | "action:<name>" */
  id: string
  kind: 'record' | 'action'
  /** The action's name ('' for the record check). */
  name: string
  label: string
  status: 'passed' | 'failed' | 'skipped'
  steps: number
  first_failure?: (UnitTestFailure & { step?: string }) | null
}

export interface UnitTestReport {
  ok: boolean
  summary: 'PASS' | 'FAIL'
  project?: string
  obj_name?: string
  passed?: number
  failed?: number
  steps?: UnitTestStep[]
  /** Absent on a result kept before checks existed (one record check). */
  checks?: UnitTestCheck[]
  first_failure?: UnitTestFailure | null
}
